import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const ROLES = [
  { value: 'viewer',  label: 'Visualizar',  description: 'Pode apenas ver o quadro e suas tarefas', color: 'bg-gray-100 text-gray-700' },
  { value: 'member',  label: 'Editar',       description: 'Pode criar e editar tarefas',             color: 'bg-blue-100 text-blue-700' },
  { value: 'admin',   label: 'Gerenciar',    description: 'Pode editar quadro e gerenciar membros',   color: 'bg-purple-100 text-purple-700' },
  { value: 'owner',   label: 'Proprietário', description: 'Controle total do quadro',                 color: 'bg-yellow-100 text-yellow-700' },
];

const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[0];

export default function ModernBoardMembers({ board, api, onMembersUpdate }) {
  const [allUsers, setAllUsers]         = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteRole, setInviteRole]     = useState('member');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [changingRole, setChangingRole] = useState({});

  useEffect(() => {
    fetchBoardMembers();
  }, [board]);

  const fetchAllUsers = async () => {
    try { const r = await api.get('/users'); setAllUsers(r.data || []); }
    catch (e) { console.error('Error fetching users:', e); }
  };

 const fetchBoardMembers = async () => {
  try {
    const [membersRes, usersRes] = await Promise.all([
      api.get(`/projects/boards/${board.id}/members`),
      api.get('/users'),
    ]);
    const users = usersRes.data || [];
    setAllUsers(users);
    const enriched = (membersRes.data || []).map(m => {
      const user = users.find(u => u.id === m.user_id || u.id === m.id);
      return {
        ...m,
        name:  m.name  || user?.name  || 'Usuário',
        email: m.email || user?.email || '',
      };
    });
    setBoardMembers(enriched);
  } catch (e) {
    console.error('Error fetching board members:', e);
  }
};

  const handleInviteMembers = async () => {
    if (selectedUsers.length === 0) { toast.error('Selecione pelo menos um usuário'); return; }
    setLoading(true);
    try {
      await api.post(`/projects/boards/${board.id}/members/bulk`, {
        user_ids: selectedUsers,
        role: inviteRole,
      });
      toast.success(`${selectedUsers.length} membro${selectedUsers.length !== 1 ? 's' : ''} convidado${selectedUsers.length !== 1 ? 's' : ''}!`);
      setSelectedUsers([]);
      setInviteRole('member');
      setShowInviteModal(false);
      fetchBoardMembers();
      onMembersUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao convidar membros');
    } finally { setLoading(false); }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remover este membro do quadro?')) return;
    try {
      await api.delete(`/projects/boards/${board.id}/members/${userId}`);
      toast.success('Membro removido');
      fetchBoardMembers();
      onMembersUpdate?.();
    } catch (e) { toast.error('Erro ao remover membro'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (newRole === 'owner') return;
    setChangingRole(prev => ({ ...prev, [userId]: true }));
    try {
      await api.put(`/projects/boards/${board.id}/members/${userId}`, { role: newRole });
      toast.success('Permissão atualizada!');
      fetchBoardMembers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao alterar permissão');
    } finally { setChangingRole(prev => ({ ...prev, [userId]: false })); }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    return parts.length > 1
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : name[0].toUpperCase();
  };

  const getUserColor = (userId) => {
    const colors = ['bg-blue-500','bg-green-500','bg-purple-500','bg-pink-500','bg-yellow-500','bg-indigo-500','bg-red-500','bg-teal-500'];
    const hash = userId?.split('').reduce((a, b) => a + b.charCodeAt(0), 0) || 0;
    return colors[hash % colors.length];
  };

  const availableUsers = allUsers.filter(u =>
    !boardMembers.some(m => m.id === u.id) &&
    (searchQuery === '' || u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Membros do Quadro</h3>
          <p className="text-sm text-gray-500 mt-1">{boardMembers.length} membro{boardMembers.length !== 1 ? 's' : ''} com acesso</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)} className="bg-blue-600 hover:bg-blue-700">
          👥 Convidar Membros
        </Button>
      </div>

      {/* Legenda de permissões */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {ROLES.map(r => (
          <div key={r.value} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.color}`}>{r.label}</span>
            <p className="text-xs text-gray-500 mt-1.5">{r.description}</p>
          </div>
        ))}
      </div>

      {/* Lista de membros */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-700">Membros Atuais</h4>
        </div>
        <div className="divide-y divide-gray-100">
          {boardMembers.map((member) => {
            const roleInfo = getRoleInfo(member.role);
            const isOwner  = member.role === 'owner';
            return (
              <div key={member.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold ${getUserColor(member.id)}`}>
                    {getUserInitials(member.name)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{member.name}</p>
                    <p className="text-xs text-gray-400">{member.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {isOwner ? (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${roleInfo.color}`}>{roleInfo.label}</span>
                  ) : (
                    <select
                      value={member.role}
                      disabled={changingRole[member.id]}
                      onChange={e => handleRoleChange(member.id, e.target.value)}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
                    >
                      {ROLES.filter(r => r.value !== 'owner').map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  )}
                  {!isOwner && (
                    <button
                      onClick={() => handleRemoveMember(member.user_id)}
                      className="text-xs text-red-400 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                    >
                      Remover
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {boardMembers.length === 0 && (
            <div className="py-10 text-center text-gray-400">
              <div className="text-3xl mb-2">👥</div>
              <p className="text-sm">Nenhum membro adicionado ainda</p>
              <p className="text-xs mt-1">Clique em "Convidar Membros" para começar</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de convite */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Convidar Membros para o Quadro</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Busca */}
            <div>
              <Label>Buscar usuários</Label>
              <Input
                placeholder="Nome ou e-mail..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Selecionados */}
            {selectedUsers.length > 0 && (
              <div>
                <Label>Selecionados ({selectedUsers.length})</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedUsers.map(uid => {
                    const u = allUsers.find(x => x.id === uid);
                    return (
                      <Badge key={uid} className="bg-blue-100 text-blue-800 border-0 gap-1">
                        {u?.name || 'Usuário'}
                        <button onClick={() => toggleUserSelection(uid)} className="hover:text-red-600 ml-1">×</button>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lista de usuários */}
            <div>
              <Label>Usuários disponíveis</Label>
              <div className="mt-2 max-h-52 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                {availableUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => toggleUserSelection(u.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 ${selectedUsers.includes(u.id) ? 'bg-blue-50' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold ${getUserColor(u.id)}`}>
                      {getUserInitials(u.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate">{u.email}</p>
                    </div>
                    {selectedUsers.includes(u.id) && (
                      <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </div>
                ))}
                {availableUsers.length === 0 && (
                  <div className="py-6 text-center text-gray-400 text-sm">
                    {searchQuery ? 'Nenhum usuário encontrado' : 'Todos os usuários já são membros'}
                  </div>
                )}
              </div>
            </div>

            {/* Seleção de permissão */}
            <div>
              <Label>Permissão dos convidados</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {ROLES.filter(r => r.value !== 'owner').map(r => (
                  <button
                    key={r.value}
                    onClick={() => setInviteRole(r.value)}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${inviteRole === r.value ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-300'}`}
                  >
                    <p className={`text-xs font-semibold ${inviteRole === r.value ? 'text-blue-700' : 'text-gray-700'}`}>{r.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-tight">{r.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInviteModal(false); setSelectedUsers([]); setSearchQuery(''); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleInviteMembers}
              disabled={selectedUsers.length === 0 || loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Convidando…' : `Convidar ${selectedUsers.length > 0 ? selectedUsers.length : ''} membro${selectedUsers.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
